import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { UiIcon, EntityIcon, SplitButton, CheckMark } from "../../Layouts/Icon";
import { copyTextToClipboard } from "../../utils/formatters";
import {
  iconLabel,
  findDuotoneSecondary,
  toIconShape,
  toSvg,
  toDataUrl,
  toJsx,
  toVue,
  toSvelte,
  toAngular,
  downloadSvg,
  downloadPng,
} from "../../utils/svgIcon";
import { STROKE, clampStroke, hasStrokeWidth } from "../../utils/strokeWidth";
import KeylinePreview from "./KeylinePreview";
import { STUDIO_PATH } from "../../constants";

// Draw the keyline grid behind the preview (set to false to show the plain icon)
const SHOW_KEYLINES = true;

const SVG_ACTIONS = [
  { id: "copy-svg", label: "Copy SVG" },
  { id: "copy-data-url", label: "Copy Data URL" },
  { id: "download-svg", label: "Download SVG" },
  { id: "download-png", label: "Download PNG" },
];

const CODE_ACTIONS = [
  { id: "copy-jsx", label: "JSX" },
  { id: "copy-vue", label: "Vue" },
  { id: "copy-svelte", label: "Svelte" },
  { id: "copy-angular", label: "Angular" },
];

// [build text from icon shape, what screen readers hear once it's copied]
const COPY_ACTIONS = {
  "copy-svg": [toSvg, "SVG copied"],
  "copy-data-url": [toDataUrl, "Data URL copied"],
  "copy-jsx": [toJsx, "JSX component copied"],
  "copy-vue": [toVue, "Vue component copied"],
  "copy-svelte": [toSvelte, "Svelte component copied"],
  "copy-angular": [toAngular, "Angular component copied"],
};

const STROKE_KEY = "icofont:stroke-width";

const readStroke = () => {
  try {
    const saved = window.localStorage.getItem(STROKE_KEY);
    return saved === null ? STROKE.base : clampStroke(saved);
  } catch (e) {
    return STROKE.base;
  }
};

const formatStroke = (width) => `${+width.toFixed(2)}px`;

// Stroke width for line icons: a slider with the value and a reset
const StrokeControl = ({ value, onChange }) => (
  <section className="icon-detail__section stroke-control">
    <div className="stroke-control__head">
      <label htmlFor="stroke-width">Stroke width</label>
      <span className="stroke-control__value">{formatStroke(value)}</span>
      <button
        type="button"
        className="icon-btn"
        onClick={() => onChange(STROKE.base)}
        disabled={value === STROKE.base}
        aria-label={`Reset stroke width to ${formatStroke(STROKE.base)}`}
        title={`Reset to ${formatStroke(STROKE.base)}`}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
          <path
            d="M13.5 8A5.5 5.5 0 1 1 11.8 4M12.5 1.5V4.5H9.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
    <input
      id="stroke-width"
      type="range"
      min={STROKE.min}
      max={STROKE.max}
      step={STROKE.step}
      value={value}
      onChange={(event) => onChange(clampStroke(event.target.value))}
      style={{
        "--fill": `${((value - STROKE.min) / (STROKE.max - STROKE.min)) * 100}%`,
      }}
      aria-valuetext={formatStroke(value)}
    />
    <p className="stroke-control__note">
      {value === STROKE.base
        ? "Copy and download use this width."
        : `Copy and download use ${formatStroke(value)}. The icon font stays ${formatStroke(STROKE.base)}.`}
    </p>
  </section>
);

StrokeControl.propTypes = {
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

const TILE_SELECTOR = ".icon__list__2 .icon[data-id]";
const SHEET_QUERY = "(max-width: 991px)";

// Font markup for the downloadable package; duotone icons need both classes
export const fontMarkup = (icon) =>
  icon.isDuotone
    ? `<i class="icofont-duotone icofont-${icon.name}"></i>`
    : `<i class="icofont-${icon.name}"></i>`;

const Chevron = ({ direction }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <path
      d={
        direction === "left" ? "M10 3.5 5.5 8l4.5 4.5" : "M6 3.5 10.5 8 6 12.5"
      }
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Pencil with sparkles, drawn like the chevrons beside it
const StudioIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M9.5 4.5 11.5 6.5M4 12l.5-2.5 6.3-6.3a1.4 1.4 0 0 1 2 2L6.5 11.5 4 12ZM3 2v2.5M1.75 3.25h2.5M12.5 10.5v3M11 12h3"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Same size and stroke as the chevrons beside it
const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M4 4l8 8M12 4l-8 8"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const CopyIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <g
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    >
      <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
      <path d="M10.5 3.5v-.5A1.5 1.5 0 0 0 9 1.5H3A1.5 1.5 0 0 0 1.5 3v6A1.5 1.5 0 0 0 3 10.5h.5" />
    </g>
  </svg>
);

// The copy button turns into a check for a moment after copying
const CopyRow = ({ label, value, onCopy, copied }) => (
  <div className={`copy-row${copied ? " is-copied" : ""}`}>
    {label ? <span className="copy-row__label">{label}</span> : null}
    <code title={value}>{value}</code>
    <button
      type="button"
      className="icon-btn"
      onClick={onCopy}
      aria-label={`Copy ${label || value}`}
      title={copied ? "Copied" : "Copy"}
    >
      {copied ? <CheckMark /> : <CopyIcon />}
    </button>
  </div>
);

// Previous / next tiles around an icon, read from the grid as currently rendered
// (it grows as more categories load, so this is never cached)
const findNeighbours = (id) => {
  const ids = Array.prototype.map.call(
    document.querySelectorAll(TILE_SELECTOR),
    (tile) => tile.getAttribute("data-id"),
  );
  const index = ids.indexOf(id);
  return {
    prev: index > 0 ? ids[index - 1] : null,
    next: index !== -1 && index < ids.length - 1 ? ids[index + 1] : null,
  };
};

const readIconParam = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    name: params.get("icon"),
    duotone: params.get("style") === "duotone",
  };
};

const writeIconParam = (icon) => {
  const params = new URLSearchParams(window.location.search);
  if (icon) {
    params.set("icon", icon.name);
    if (icon.isDuotone) params.set("style", "duotone");
    else params.delete("style");
  } else {
    params.delete("icon");
    params.delete("style");
  }
  const query = params.toString();
  const url =
    window.location.pathname +
    (query ? "?" + query : "") +
    window.location.hash;
  if (
    url !==
    window.location.pathname + window.location.search + window.location.hash
  ) {
    window.history.replaceState(window.history.state, "", url);
  }
};

export const IconDetail = ({
  icon,
  icons,
  inCollection,
  onOpen,
  onClose,
  onToggleCollection,
  showError,
}) => {
  // Re-render once after the grid paints so prev / next see the latest tiles
  const [, setGridTick] = useState(0);
  // The action that just ran, confirmed on its own button for a moment (no toast)
  const [done, setDone] = useState(null);
  const [announcement, setAnnouncement] = useState("");
  const doneTimer = useRef(null);
  const [strokeWidth, setStrokeWidth] = useState(STROKE.base);
  const closeRef = useRef(null);
  const panelRef = useRef(null);
  const lastIconId = useRef(null);
  const urlHandled = useRef(false);

  // Open the icon named in ?icon= once it has loaded. The first icons to arrive are only
  // the opening categories, so an icon that isn't there yet is looked for again when the
  // full list comes in.
  useEffect(() => {
    if (urlHandled.current || !icons.length) return;
    const { name, duotone } = readIconParam();
    if (!name) {
      urlHandled.current = true;
      return;
    }
    const match = icons.find(
      (item) =>
        item.name === name &&
        !!item.isDuotone === duotone &&
        item.assign !== "after",
    );
    if (match) {
      urlHandled.current = true;
      onOpen(match);
    }
  }, [icons]);

  // Keep the URL, body class, focus and prev/next in step with the open icon
  useEffect(() => {
    if (icon) {
      writeIconParam(icon);
      document.body.classList.add("icon-detail-open");
      window.requestAnimationFrame(() => setGridTick((tick) => tick + 1));
      if (
        !lastIconId.current &&
        window.matchMedia(SHEET_QUERY).matches &&
        closeRef.current
      ) {
        closeRef.current.focus();
      }
    } else if (lastIconId.current) {
      writeIconParam(null);
      document.body.classList.remove("icon-detail-open");
      const tile = document.querySelector(
        `.icon[data-id="${lastIconId.current}"] .icon__open`,
      );
      if (tile) tile.focus();
    }
    lastIconId.current = icon ? icon.id : null;
  }, [icon]);

  useEffect(() => () => document.body.classList.remove("icon-detail-open"), []);
  useEffect(() => () => clearTimeout(doneTimer.current), []);
  // A new icon starts with no pending confirmation
  useEffect(() => setDone(null), [icon && icon.id]);

  // The chosen stroke width carries over between icons and visits
  useEffect(() => setStrokeWidth(readStroke()), []);
  const changeStroke = (width) => {
    setStrokeWidth(width);
    try {
      window.localStorage.setItem(STROKE_KEY, String(width));
    } catch (e) {
      // Private mode or storage blocked: the width just isn't remembered
    }
  };

  // The panel floats over the bottom of the grid. Its height goes in --detail-h, so the
  // page can scroll the last rows (and the open icon) clear of it
  useEffect(() => {
    if (!icon || !panelRef.current) return undefined;
    const root = document.documentElement;
    const measure = () => {
      if (panelRef.current)
        root.style.setProperty(
          "--detail-h",
          panelRef.current.offsetHeight + "px",
        );
    };
    measure();
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measure)
        : null;
    if (observer) observer.observe(panelRef.current);
    return () => {
      if (observer) observer.disconnect();
      root.style.removeProperty("--detail-h");
    };
  }, [!!icon]);

  // An icon opened under the panel scrolls into view
  useEffect(() => {
    if (!icon || window.matchMedia(SHEET_QUERY).matches) return;
    const frame = window.requestAnimationFrame(() => {
      const tile = document.querySelector(`.icon[data-id="${icon.id}"]`);
      if (tile) tile.scrollIntoView({ block: "nearest", behavior: "smooth" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [icon && icon.id]);

  const step = (id) => {
    if (!id) return;
    const target = icons.find((item) => item.id === id);
    if (!target) return;
    onOpen(target);
  };

  // Esc closes; arrow keys move to the previous / next icon
  useEffect(() => {
    if (!icon) return undefined;
    const onKey = (event) => {
      const tag = event.target.tagName;
      if (
        event.defaultPrevented ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey
      )
        return;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      const neighbours = findNeighbours(icon.id);
      if (event.key === "Escape") {
        // An open menu closes first; the next Esc closes the panel
        if (document.querySelector(".icon-detail .split-btn.open")) return;
        event.preventDefault();
        onClose();
      } else if (event.key === "ArrowLeft" && neighbours.prev) {
        event.preventDefault();
        step(neighbours.prev);
      } else if (event.key === "ArrowRight" && neighbours.next) {
        event.preventDefault();
        step(neighbours.next);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [icon]);

  if (!icon) return null;

  const neighbours = findNeighbours(icon.id);
  const label = iconLabel(icon.name);
  const code = icon.code.toString(16);
  const confirm = (id, message) => {
    setDone(id);
    setAnnouncement(message);
    clearTimeout(doneTimer.current);
    doneTimer.current = setTimeout(() => setDone(null), 1600);
  };
  const copy = (text, message, id) => {
    copyTextToClipboard(text);
    confirm(id, message);
  };

  const adjustable = hasStrokeWidth(icon);
  const runAction = (action) => {
    const shape = toIconShape(icon, findDuotoneSecondary(icons, icon), {
      strokeWidth: adjustable ? strokeWidth : STROKE.base,
    });
    if (COPY_ACTIONS[action]) {
      const [build, message] = COPY_ACTIONS[action];
      copy(build(shape), message, action);
    } else if (action === "download-svg") {
      downloadSvg(shape);
      confirm(action, "SVG downloaded");
    } else if (action === "download-png") {
      downloadPng(shape).then(
        () => confirm(action, "PNG downloaded"),
        () => showError("Couldn't create the PNG. Try again."),
      );
    }
  };

  return (
    <React.Fragment>
      <div className="icon-detail__backdrop" onClick={onClose} />
      <aside
        className="icon-detail"
        role="dialog"
        aria-labelledby="icon-detail-title"
        ref={panelRef}
      >
        <div className="icon-detail__bar">
          <button
            type="button"
            className="icon-btn"
            onClick={() => step(neighbours.prev)}
            disabled={!neighbours.prev}
            aria-label="Previous icon"
            title="Previous icon (←)"
          >
            <Chevron direction="left" />
          </button>
          <button
            type="button"
            className="icon-btn"
            onClick={() => step(neighbours.next)}
            disabled={!neighbours.next}
            aria-label="Next icon"
            title="Next icon (→)"
          >
            <Chevron direction="right" />
          </button>
          <span className="icon-detail__spacer" />
          <Link to={`${STUDIO_PATH}?icon=${icon.code}`} className="icon-detail__studio">
            <StudioIcon />
            Edit in studio
          </Link>
          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            aria-label="Close"
            title="Close (Esc)"
            ref={closeRef}
          >
            <CloseIcon />
          </button>
        </div>

        <span className="sr-only" role="status" aria-live="polite">
          {done ? announcement : ""}
        </span>
        <div className="icon-detail__body">
          <div className="icon-detail__preview">
            {SHOW_KEYLINES ? (
              <KeylinePreview
                icon={icon}
                icons={icons}
                strokeWidth={strokeWidth}
              />
            ) : (
              <EntityIcon entity={icon} />
            )}
          </div>
          <div className="icon-detail__main">
            <h2 className="icon-detail__title" id="icon-detail-title">
              {label}
            </h2>
            <p className="icon-detail__meta">
              {iconLabel(icon.cat)}
              {icon.isDuotone && icon.cat !== "duotone" ? " · duotone" : ""}
            </p>

            {adjustable ? (
              <StrokeControl value={strokeWidth} onChange={changeStroke} />
            ) : null}

            <div className="icon-detail__actions">
              <SplitButton
                options={SVG_ACTIONS}
                storageKey="icofont:svg-action"
                label="More SVG options"
                onSelect={runAction}
                doneId={done}
                doneLabel={
                  done && done.indexOf("download") === 0
                    ? "Downloaded"
                    : "Copied"
                }
              />
              <button
                type="button"
                className={`detail-btn${inCollection ? " is-added" : ""}`}
                onClick={() => onToggleCollection(icon, inCollection)}
                aria-pressed={inCollection}
              >
                <UiIcon name={inCollection ? "check" : "plus"} />
                {inCollection ? "In your font" : "Add to font"}
              </button>
            </div>

            <section className="icon-detail__section">
              <h3>Use in code</h3>
              <div className="icon-detail__code-grid">
                {CODE_ACTIONS.map((action) => (
                  <button
                    key={action.id}
                    type="button"
                    className={`detail-btn${done === action.id ? " is-done" : ""}`}
                    onClick={() => runAction(action.id)}
                    title={`Copy as ${action.label} component`}
                  >
                    {done === action.id ? (
                      <React.Fragment>
                        <CheckMark />
                        Copied
                      </React.Fragment>
                    ) : (
                      action.label
                    )}
                  </button>
                ))}
              </div>
            </section>
          </div>

          <div className="icon-detail__side">
            <section className="icon-detail__section">
              <h3>Use with the icon font</h3>
              <CopyRow
                value={fontMarkup(icon)}
                copied={done === "copy-markup"}
                onCopy={() =>
                  copy(fontMarkup(icon), "Markup copied", "copy-markup")
                }
              />
            </section>

            <section className="icon-detail__section">
              <h3>Codes</h3>
              <CopyRow
                label="Hex"
                value={code}
                copied={done === "copy-hex"}
                onCopy={() => copy(code, "Hex code copied", "copy-hex")}
              />
              <CopyRow
                label="HTML entity"
                value={`&#x${code};`}
                copied={done === "copy-entity"}
                onCopy={() =>
                  copy(`&#x${code};`, "HTML entity copied", "copy-entity")
                }
              />
            </section>
          </div>
        </div>
      </aside>
    </React.Fragment>
  );
};

IconDetail.propTypes = {
  icon: PropTypes.object,
  icons: PropTypes.array.isRequired,
  inCollection: PropTypes.bool,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onToggleCollection: PropTypes.func.isRequired,
  showError: PropTypes.func.isRequired,
};

export default IconDetail;
